import * as React from 'react'

import { Button } from '~/client/src/shared/components/Button/Button'

import { cn } from '../../utils/cn'
import ExpandToggle from './ExpandToggle'
import GroupCount from './GroupCount'
import GroupLabel from './GroupLabel'

interface IGroupHeaderTriggerProps {
  /** Drives `aria-expanded` and the chevron direction. */
  expanded: boolean
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  ariaLabel: string
  className?: string
  /** Rendered before the chevron, inside the click target (e.g. LBS spacer). */
  leading?: React.ReactNode
  /** Canonical: pass `label`. Custom: pass `children` — canonical wins. */
  label?: React.ReactNode
  labelIcon?: React.ReactNode
  /** Canonical only: overrides the label text's colour. */
  labelClassName?: string
  count?: number | string
  children?: React.ReactNode
}

export default function GroupHeaderTrigger({
  expanded,
  onClick,
  ariaLabel,
  className,
  leading,
  label,
  labelIcon,
  labelClassName,
  count,
  children,
}: IGroupHeaderTriggerProps): React.ReactElement {
  const isCanonical = label !== undefined
  const body = isCanonical ? (
    <span className="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-2 tw:overflow-hidden">
      <GroupLabel icon={labelIcon} className={labelClassName}>
        {label}
      </GroupLabel>
      {count != null && <GroupCount>{count}</GroupCount>}
    </span>
  ) : (
    children
  )
  return (
    <Button
      variant="ghost"
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      aria-label={ariaLabel}
      className={cn(
        // `gap-0 p-0` keep strict 16px-per-level depth math (a uniform flex gap would add
        // 8px between the indent and the chevron); the chevron carries its own 8px gap.
        'tw:h-full tw:min-w-0 tw:flex-1',
        'tw:justify-start tw:text-left',
        'tw:gap-0 tw:p-0',
        // The band owns its background: cancel ghost Button's hover and
        // `aria-expanded` fills, which otherwise paint a muted box on open groups.
        'tw:hover:bg-transparent tw:aria-expanded:bg-transparent',
        // Legacy `svg-inline-react` (TagIcon) workaround — classless inner SVGs must
        // fill their inline-sized `<i>` wrapper instead of being clamped to size-4.
        // Lucide icons live under `<span>` so are untouched.
        'tw:[&_i_svg]:!size-full',
        // Figma: the band chevron is foreground at 85%.
        'tw:[&>span[aria-hidden=true]>svg]:opacity-85',
        className,
      )}
    >
      {leading}
      <span
        aria-hidden="true"
        className="tw:mr-2 tw:flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center"
      >
        <ExpandToggle expanded={expanded} />
      </span>
      {body}
    </Button>
  )
}
